import SelectBuscable from '@/shared/components/SelectBuscable'
import { CapaDeModal } from '@/shared/components/CapaDeModal'
import React, { useState, useMemo, useEffect } from "react";
import {
  X,
  Loader2,
  Save,
  Search,
  Info,
  FileText,
  Paperclip,
  Trash2,
} from "lucide-react";
import type { Invoice } from '@/types/purchaseOrderRH.types';
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton';

// Ícono de calendario (esquina doblada) — mismo que los filtros de fecha de /pagos.
const CalendarFolded = ({
  className,
  ...props
}: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <path d="M16 3H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12l5-5V5a2 2 0 0 0-2-2Z" />
    <path d="M21 14h-5a2 2 0 0 0-2 2v5" />
    <path d="M8 2v4" />
    <path d="M16 2v4" />
    <path d="M2 9h19" />
  </svg>
);

// ISO (yyyy-mm-dd) → dd/mm/aaaa para el overlay del input de fecha.
const formatDateDDMMYYYY = (iso: string) => {
  const [y, m, d] = (iso || "").split("-");
  if (!y || !m || !d) return "";
  return `${d}/${m}/${y}`;
};

const todayISO = () => new Date().toISOString().split("T")[0];

// $ con formato es-MX y signo delante del símbolo (para diferencias negativas).
const formatCurrency = (value: string | number | null | undefined) => {
  const num =
    typeof value === "string" ? parseFloat(value) : Number(value || 0);
  const safe = isNaN(num) ? 0 : num;
  const sign = safe < 0 ? "-" : "";
  return `${sign}$${Math.abs(safe).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Estilo de inputs homologado con Almacén (relleno #F6F9FB, sin borde).
const INPUT_CLASS =
  "w-full px-3 py-2.5 rounded-lg text-sm text-gray-800 bg-[#F6F9FB] border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-gray-400 disabled:opacity-60 disabled:cursor-not-allowed";

// Una factura pagable: Invoice + los posibles campos de proveedor/OC según la
// vista que abra el modal (la vista global de /pagos trae supplierName/poId; las
// vistas por OC traen sólo el Invoice de esa orden).
type PayableInvoice = Invoice & {
  supplierId?: string;
  supplierName?: string;
  supplierRfc?: string;
  poId?: string;
  poNumber?: string;
  purchaseOrder?: {
    id?: string;
    supplier?: {
      id?: string;
      legal_name?: string;
      company_name?: string;
      rfc?: string;
    };
  };
};

interface AddPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: PaymentFormData) => Promise<void>;
  invoices: PayableInvoice[];
  preselectedInvoiceId?: string;
}

export interface PaymentFormData {
  amount: number;
  payment_date: string;
  payment_method?: string;
  reference_number?: string;
  notes?: string;
  // back-compat: primera factura cubierta (para el endpoint actual de 1 factura).
  invoiceId?: string;
  // SP-207: todas las facturas que cubre el pago.
  invoiceIds: string[];
  // Comprobante de la transferencia o la aplicacion de pago del ERP. Opcional, y
  // va ligado al pago completo, no a cada factura.
  receipt?: File | null;
  supplierId?: string;
  supplierName?: string;
}

// Saldo pendiente de una factura = total - pagos ya aplicados.
const invoiceRemaining = (inv: PayableInvoice) => {
  const total = inv.invoice_total != null ? Number(inv.invoice_total) : 0;
  const paid =
    inv.payments?.reduce((sum, p) => sum + (Number(p.amount) || 0), 0) || 0;
  return Math.max(0, total - paid);
};

// Deriva el proveedor de una factura, tolerando las distintas formas en que
// llega según la vista. Si no hay dato de proveedor (vistas por OC), agrupa todo
// bajo un mismo proveedor genérico para que el modal siga funcionando.
const supplierOf = (inv: PayableInvoice): { id: string; name: string } => {
  const name =
    inv.supplierName ||
    inv.purchaseOrder?.supplier?.legal_name ||
    inv.purchaseOrder?.supplier?.company_name ||
    "";
  const id =
    inv.supplierId || inv.purchaseOrder?.supplier?.id || name || "__default__";
  return { id: String(id), name: name || "Proveedor" };
};

const invoiceLabel = (inv: PayableInvoice) =>
  inv.xml_file_name ||
  inv.supplier_invoice_number ||
  inv.fiscal_folio ||
  "Factura";

export const AddPaymentModal: React.FC<AddPaymentModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  invoices,
  preselectedInvoiceId,
}) => {
  const [supplierId, setSupplierId] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [showList, setShowList] = useState(false);
  const [amount, setAmount] = useState<number>(0);
  const [paymentDate, setPaymentDate] = useState(todayISO());
  const [paymentMethod, setPaymentMethod] = useState("");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [receipt, setReceipt] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Proveedores únicos a partir de las facturas recibidas.
  const providers = useMemo(() => {
    const map = new Map<string, string>();
    invoices.forEach((inv) => {
      const { id, name } = supplierOf(inv);
      if (id && !map.has(id)) map.set(id, name);
    });
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [invoices]);

  // Reset + preselección al abrir. Si viene una factura preseleccionada, se fija
  // su proveedor y se agrega a las facturas cubiertas.
  useEffect(() => {
    if (!isOpen) return;
    const pre = preselectedInvoiceId
      ? invoices.find((i) => i.id === preselectedInvoiceId)
      : undefined;
    if (pre) {
      setSupplierId(supplierOf(pre).id);
      setSelectedIds([pre.id]);
    } else {
      setSupplierId(providers.length === 1 ? providers[0].id : "");
      setSelectedIds([]);
    }
    setSearch("");
    setShowList(false);
    setAmount(0);
    setPaymentDate(todayISO());
    setPaymentMethod("");
    setReference("");
    setNotes("");
    setReceipt(null);
    setError(null);
    setSubmitting(false);
    // Sólo al abrir o cambiar la preselección.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, preselectedInvoiceId]);

  // Facturas del proveedor elegido que aún tienen saldo.
  const providerInvoices = useMemo(
    () =>
      invoices.filter(
        (inv) =>
          supplierOf(inv).id === supplierId && invoiceRemaining(inv) > 0.005,
      ),
    [invoices, supplierId],
  );

  const selectedInvoices = useMemo(
    () =>
      selectedIds
        .map((id) => invoices.find((i) => i.id === id))
        .filter(Boolean) as PayableInvoice[],
    [selectedIds, invoices],
  );

  const availableToAdd = useMemo(() => {
    const q = search.trim().toLowerCase();
    return providerInvoices
      .filter((inv) => !selectedIds.includes(inv.id))
      .filter((inv) => !q || invoiceLabel(inv).toLowerCase().includes(q));
  }, [providerInvoices, selectedIds, search]);

  const facturasTotal = useMemo(
    () => selectedInvoices.reduce((sum, inv) => sum + invoiceRemaining(inv), 0),
    [selectedInvoices],
  );
  const diferencia = (amount || 0) - facturasTotal;
  const balanceado = Math.abs(diferencia) < 0.005;

  const providerName = providers.find((p) => p.id === supplierId)?.name || "";
  const canSubmit =
    !!supplierId && amount > 0 && selectedIds.length > 0 && !submitting;

  const addInvoice = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    setSearch("");
    setShowList(false);
  };
  const removeInvoice = (id: string) =>
    setSelectedIds((prev) => prev.filter((x) => x !== id));
  const changeProvider = (id: string) => {
    setSupplierId(id);
    setSelectedIds([]);
    setSearch("");
  };

  // Mensaje contextual (banner ámbar) según lo que falte capturar.
  const bannerMsg = (() => {
    if (amount <= 0 && selectedIds.length === 0)
      return "Captura el monto del pago y agrega las facturas que cubre";
    if (selectedIds.length === 0)
      return "Agrega las facturas que cubre este pago";
    if (amount <= 0) return "Captura el monto del pago";
    if (!balanceado)
      return `El monto del pago no coincide con el total de facturas (diferencia ${formatCurrency(
        Math.abs(diferencia),
      )})`;
    return null;
  })();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supplierId) return setError("Selecciona un proveedor");
    if (selectedIds.length === 0)
      return setError("Agrega al menos una factura");
    if (amount <= 0) return setError("El monto debe ser mayor a 0");
    try {
      setSubmitting(true);
      setError(null);
      await onSubmit({
        amount,
        payment_date: paymentDate,
        payment_method: paymentMethod || undefined,
        reference_number: reference || undefined,
        notes: notes || undefined,
        invoiceIds: [...selectedIds],
        invoiceId: selectedIds[0],
        receipt,
        supplierId,
        supplierName: providerName,
      });
      onClose();
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Error al registrar el pago",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <CapaDeModal>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl overflow-hidden">
        {/* Wrapper con el scroll adentro para que el redondeo de las 4 esquinas
            no lo "recorte" la barra de scroll. */}
        <div className="max-h-[92vh] overflow-y-auto">
          {/* Header */}
          <div className="p-6 pb-0">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  Agregar pago
                </h2>
                <p className="text-sm text-gray-400 mt-0.5">
                  Un pago puede cubrir varias facturas de un mismo proveedor
                </p>
              </div>
              <button
                onClick={onClose}
                className="flex-shrink-0 w-7 h-7 rounded-full bg-[#ECECEC] hover:bg-[#E2E2E2] flex items-center justify-center transition-colors"
                aria-label="Cerrar"
              >
                <X className="w-4 h-4 text-[#C4C4C4]" strokeWidth={2.5} />
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            {/* Proveedor + Monto */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Proveedor <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <SelectBuscable
                    variante="formulario"
                    value={supplierId}
                    onChange={changeProvider}
                    placeholder="Selecciona el proveedor"
                    placeholderDeBusqueda="Buscar proveedor…"
                    opciones={providers.map((p) => ({
                      value: String(p.id),
                      label: p.name,
                    }))}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Monto (MXN) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={amount || ""}
                  onChange={(e) => setAmount(parseFloat(e.target.value) || 0)}
                  className={INPUT_CLASS}
                  placeholder="$0.00"
                />
              </div>
            </div>

            {/* Fecha + Método */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha de pago <span className="text-red-500">*</span>
                </label>
                <div className="relative bg-[#F6F9FB] rounded-lg">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm pointer-events-none select-none">
                    {paymentDate ? (
                      <span className="text-gray-800">
                        {formatDateDDMMYYYY(paymentDate)}
                      </span>
                    ) : (
                      <span className="text-gray-400">dd/mm/aaaa</span>
                    )}
                  </span>
                  <CalendarFolded className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-500 pointer-events-none" />
                  <input
                    type="date"
                    value={paymentDate}
                    onChange={(e) => setPaymentDate(e.target.value)}
                    onClick={(e) =>
                      (
                        e.currentTarget as HTMLInputElement & {
                          showPicker?: () => void;
                        }
                      ).showPicker?.()
                    }
                    className="w-full px-3 py-2.5 rounded-lg border-0 opacity-0 cursor-pointer"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Método de pago (opcional)
                </label>
                <div className="relative">
                  <SelectBuscable
                    variante="formulario"
                    value={paymentMethod}
                    onChange={setPaymentMethod}
                    placeholder="Selecciona el método de pago"
                    placeholderDeBusqueda="Buscar método…"
                    opciones={[
                      "Transferencia bancaria",
                      "Cheque",
                      "Efectivo",
                      "Tarjeta de crédito",
                      "Tarjeta de débito",
                      "Depósito",
                      "SPEI",
                    ].map((metodo) => ({ value: metodo, label: metodo }))}
                  />
                </div>
              </div>
            </div>

            {/* Número de referencia */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Número de referencia (opcional)
              </label>
              <input
                type="text"
                maxLength={50}
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                className={INPUT_CLASS}
                placeholder="EJ: REF-1234"
              />
            </div>

            {/* Facturas cubiertas */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Facturas cubiertas <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setShowList(true);
                  }}
                  onFocus={() => setShowList(true)}
                  onBlur={() => setTimeout(() => setShowList(false), 150)}
                  disabled={!supplierId}
                  placeholder={
                    supplierId
                      ? "Busca y agrega facturas..."
                      : "Primero selecciona un proveedor"
                  }
                  className={`${INPUT_CLASS} pr-9`}
                />
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />

                {showList && supplierId && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
                    {availableToAdd.length > 0 ? (
                      availableToAdd.map((inv) => (
                        <button
                          type="button"
                          key={inv.id}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => addInvoice(inv.id)}
                          className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center justify-between gap-3"
                        >
                          <span className="text-sm text-gray-800 truncate">
                            {invoiceLabel(inv)}
                          </span>
                          <span className="text-xs text-gray-500 flex-shrink-0">
                            {formatCurrency(invoiceRemaining(inv))}
                          </span>
                        </button>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-sm text-gray-400">
                        {providerInvoices.length === 0
                          ? "Este proveedor no tiene facturas con saldo"
                          : "No hay más facturas para agregar"}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Facturas seleccionadas */}
              {selectedInvoices.length > 0 && (
                <div className="mt-2 space-y-2">
                  {selectedInvoices.map((inv) => (
                    <div
                      key={inv.id}
                      className="flex items-center gap-3 px-3 py-2 bg-[#F6F9FB] rounded-lg"
                    >
                      <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">
                          {invoiceLabel(inv)}
                        </p>
                        <p className="text-xs text-gray-500">
                          Saldo: {formatCurrency(invoiceRemaining(inv))}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeInvoice(inv.id)}
                        className="text-gray-400 hover:text-red-500 flex-shrink-0"
                        aria-label="Quitar factura"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Conciliación */}
            <div className="rounded-lg border border-gray-200 overflow-hidden bg-[#FAF9FF]">
              <div className="px-4 py-2.5 border-b border-gray-200">
                <span className="text-sm font-semibold text-gray-700">
                  Conciliación
                </span>
              </div>
              <div className="px-4 py-3 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Monto del pago</span>
                  <span className="font-medium text-gray-800">
                    {formatCurrency(amount)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Monto de facturas</span>
                  <span className="font-medium text-gray-800">
                    {formatCurrency(facturasTotal)}
                  </span>
                </div>
                <div className="flex justify-between pt-2 border-t border-gray-200">
                  <span className="font-semibold text-gray-700">
                    Diferencia
                  </span>
                  <span
                    className={`font-bold ${balanceado ? "text-green-600" : "text-red-500"}`}
                  >
                    {formatCurrency(diferencia)}
                  </span>
                </div>
              </div>
            </div>

            {/* Banner contextual */}
            {bannerMsg && (
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-sm">
                <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{bannerMsg}</span>
              </div>
            )}

            {/* Comprobante del pago */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Comprobante de pago (opcional)
              </label>
              {receipt ? (
                <div className="flex items-center justify-between gap-3 px-3 py-2.5 bg-[#F6F9FB] border border-gray-200 rounded-lg">
                  <span className="flex items-center gap-2 min-w-0 text-sm text-gray-700">
                    <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="truncate">{receipt.name}</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => setReceipt(null)}
                    className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                    title="Quitar comprobante"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label className="flex items-center gap-2 px-3 py-2.5 bg-[#F6F9FB] border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 cursor-pointer hover:border-[#253579] hover:text-gray-700 transition-colors">
                  <Paperclip className="w-4 h-4" />
                  Adjunta el PDF de la transferencia o la aplicación de pago
                  <input
                    type="file"
                    accept="application/pdf,.pdf"
                    className="hidden"
                    onChange={(e) => {
                      const archivo = e.target.files?.[0];
                      // El input se limpia siempre: si no, elegir el mismo
                      // archivo despues de un error no vuelve a disparar el
                      // change y parece que el boton no hace nada.
                      e.target.value = "";
                      if (!archivo) return;
                      if (archivo.type !== "application/pdf") {
                        setError("El comprobante debe ser un PDF");
                        return;
                      }
                      setError(null);
                      setReceipt(archivo);
                    }}
                  />
                </label>
              )}
            </div>

            {/* Notas */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notas (opcional)
              </label>
              <textarea
                maxLength={500}
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className={`${INPUT_CLASS} resize-none`}
                placeholder="Información adicional sobre el pago..."
              />
            </div>

            {/* Acciones */}
            <div className="flex justify-start gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className={estiloDeBoton('secundario')}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={!canSubmit}
                className={estiloDeBoton('primario')}
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Guardar pago
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </CapaDeModal>
  );
};
